/**
 * pages/login.ts —— 登录页
 *
 * 🔴 2026-09-29 改版（用户拍板，设计稿「D+ · 光环之门 + 粒子 π」）：
 *   左右两栏 → **满屏深色舞台 + 居中毛玻璃登录卡**。桌面：标题在上、卡片居中、六条亮点横排、
 *   底部口号；手机（≤1000px）：标题 + 卡片 + 口号，亮点不显示（用户确认：手机上像按钮却点不了）。
 *   动效全部在 `shared/loginfx.ts`（Canvas：光环 + 标题里的粒子 π），样式在 `styles/03_login.css`。
 *   ⚠ 登录页整页是**视觉例外**（圆角 / 渐变 / 循环动画 / 暖橙范围），边界见 `05` §三，**不许推广到进系统后的页面**。
 *   ⚠ 2026-08-20 那版的点阵、π 数字流、左下光晕随本次改版**删除**；别按旧注释或一期 HTML 加回来。
 *   ⚠ π 字仍是 DOM 里的真实文字（透明），粒子画在它上面——占位、字距、读屏都靠它，别换成纯画布。
 *
 * 🔴 与一期最大的差别：**一期是演示登录门面，写死账号密码、存 sessionStorage。**
 *   二期是真登录：POST /api/auth/login → bcrypt 比对 → httpOnly Cookie 会话（T5）。
 *   ⚠ 登录名是 **oaAccount**。⚠ 2026-09-09 起真实名单的账号口径是**员工编号**
 *     （纯数字，如 46087），与 `empNo` 同值——但登录查的仍然只有 `oaAccount` 一个字段。
 *     这一条 2026-08-05 管理员当面更正过，写成 empNo 所有人都登不进来。
 *
 * 🔴 2026-08-26 用户拍板：**去掉首登强制改密**（改密改为线下通知），登录页改为
 *   「登录」与「修改密码」两张表单，由 `state.lg.mode` 切换，仍然复用 #/login 这一个路由。
 *   ⚠ 自助改密**必须报上当前密码**：前端先 `login(acct, old)` 换一次会话，
 *     再 `PUT /api/auth/password`，最后 **logout**。免旧密码的 `/password/first` 已删——
 *     它敢不校验旧密码，靠的正是「只有首登态能调」这个前提，前提没了它就是个后门。
 *   ⚠ 改完不进系统：来改密码的人多半只是来改密码的，成功后回登录页提示用新密码登录。
 */
import { definePage } from './types.js';
import { esc } from '../shared/dom.js';
import { IC, NAV } from '../shared/icons.js';
import { state } from '../shared/state.js';
import * as meta from '../shared/meta.js';
import { auth } from '../api/index.js';
import { errText } from '../api/client.js';
import { mountLoginFx } from '../shared/loginfx.js';
// ⚠ 这里与 router 构成循环引用（router → render → registry → login → router），
//   ESM 允许，因为两个符号都只在【运行时函数体里】调用，模块初始化阶段不会用到。
//   ⚠ 别把它们提到模块顶层去求值（如 `const g = go`），那一刻还是 undefined。
import { go, retryCurrent } from '../router/index.js';

/* 看板亮点：六条，说明「这个看板能做什么」（桌面横排一行；手机不显示）。
   ⚠⚠ 这里一律不出现具体业务数字——登录页是【未登录】状态，露出队伍数、提交率这类
     全营真实数字不合适。写能力、不写数据，别"顺手补个数字上去"。
   图标默认复用 NAV 的路径数据，不另画一套（下标：0 总览 / 2 OKR / 3 双周报 / 4 打卡 / 5 快报）。
   ⚠ **AI 助手不在 NAV 里**（它是右下角浮层，不是侧栏条目），所以那一行的图标是就地写的
     16 格火花，形制照 `shared/assistant.ts` 的 `ICON_SPARK`——**别为它去 NAV 里加一条**，
     那会在侧栏凭空多出一个进不去的入口。 */
const SPARK16 =
  '<path d="M7.1 2.6 8.2 5.9 11.5 7 8.2 8.1 7.1 11.4 6 8.1 2.7 7 6 5.9z"/>' +
  '<path d="M12.3 10.1l.5 1.4 1.4.5-1.4.5-.5 1.4-.5-1.4-1.4-.5 1.4-.5z"/>';

function highlightsHtml(): string {
  /* [图标路径, 名称, 说明]；NAV 那几行直接引用，避免同一枚图标出现两份坐标。
     ⚠ 说明文字 2026-09-29 由用户缩短过三条（OKR / 打卡 / AI 助手），保证横排时一行放下 */
  const rows: ReadonlyArray<readonly [string, string, string]> = [
    [NAV[0][1], '运营总览', '可视化全训练营多维运营矩阵'],
    [NAV[2][1], '团队 OKR', '目标与结果的进度、风险持续跟踪'],
    [NAV[3][1], '双周报', '按周期填报、同步关键结果进度'],
    [NAV[4][1], '日常打卡', '活动记录、日常讨论与参与统计'],
    [NAV[5][1], '项目快报', 'AI 一键生成可编辑的项目运营简报'],
    [SPARK16, 'AI 助手', 'AI 生成项目周报，查询课题进展'],
  ];
  const ic = (d: string): string =>
    `<span class="hi-i"><svg viewBox="0 0 16 16" fill="none" stroke="currentColor"
      stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round">${d}</svg></span>`;
  return `<ul class="lg-hi">${rows
    .map(([d, name, desc]) => `<li>${ic(d)}<b>${esc(name)}</b><s>${esc(desc)}</s></li>`)
    .join('')}</ul>`;
}

/* 舞台的上半截（标题）与下半截（亮点 + 口号），中间夹表单卡。
   两块画布：back 在最底层，front 压在卡片之上（光环前半圈从卡片前经过）；都不接收指针。
   ⚠ `<i class="pi">π<canvas>` 之间不能有空白：`.pi` 是行内元素，多一个空格字距就变了 */
const stageTop = `<canvas class="lg-fx lg-fx-back" aria-hidden="true"></canvas>
  <header class="lg-hd">
    <div class="lg-hero">澎<i class="pi">π<canvas class="pi-fx" aria-hidden="true"></canvas></i>计划</div>
    <h1>青年人才 AI 训练营<em>运营看板</em></h1>
    <div class="lg-en">AI Training Camp · Operations Dashboard</div>
  </header>`;

const stageBottom = `<canvas class="lg-fx lg-fx-front" aria-hidden="true"></canvas>
  ${highlightsHtml()}
  <footer class="lg-end">
    <span class="lg-slogan"><i aria-hidden="true"></i>一站式训练营全周期管理</span>
    <b>以澎湃，向未来</b>
  </footer>`;

function loginForm(): string {
  const bad = state.lg.field;
  return `<form class="lg-box" data-lg-form autocomplete="off">
    <h2>登录运营看板</h2>
    <p class="lg-note">账号是你的员工编号；首次登录后建议修改初始密码。</p>
    ${okLine()}

    <div class="lgf ${bad === 'acct' ? 'bad' : ''}">
      <label for="lg-acct">账号<b>*</b></label>
      <div class="wrap"><input id="lg-acct" name="acct" type="text" value="${esc(state.lg.acct)}"
        placeholder="员工编号，如 46087" autocomplete="username" required></div>
    </div>

    <div class="lgf ${bad === 'pwd' ? 'bad' : ''}">
      <label for="lg-pwd">密码<b>*</b></label>
      <div class="wrap eye">
        <input id="lg-pwd" name="pwd" type="${state.lg.pwd ? 'text' : 'password'}"
          placeholder="初始密码由管理员提供" autocomplete="current-password" required>
        <button type="button" class="lg-eye" data-lg="eye"
          aria-label="${state.lg.pwd ? '隐藏密码' : '显示密码'}">${state.lg.pwd ? IC.eyeOff : IC.eye}</button>
      </div>
    </div>

    ${errLine()}

    <div class="lg-row">
      <span class="lg-help">忘记密码请联系项目组管理员重置</span>
      <button type="button" class="lg-link" data-lg="to-pwd">修改密码</button>
    </div>
    <button type="submit" class="lg-sub">登 录</button>
  </form>`;
}

function changePwdForm(): string {
  const bad = state.lg.field;
  return `<form class="lg-box" data-lg-pwd autocomplete="off">
    <h2>修改密码</h2>
    <p class="lg-note">用当前密码验证身份后修改；改完请用新密码重新登录。</p>

    <div class="lgf ${bad === 'acct' ? 'bad' : ''}">
      <label for="cp-acct">账号<b>*</b></label>
      <div class="wrap"><input id="cp-acct" name="acct" type="text" value="${esc(state.lg.acct)}"
        placeholder="员工编号，如 46087" autocomplete="username" required></div>
    </div>

    <div class="lgf ${bad === 'old' ? 'bad' : ''}">
      <label for="cp-old">当前密码<b>*</b></label>
      <div class="wrap"><input id="cp-old" name="op" type="password"
        placeholder="初始密码由管理员提供" autocomplete="current-password" required></div>
    </div>

    <div class="lgf ${bad === 'np' ? 'bad' : ''}">
      <label for="cp-new">新密码<b>*</b></label>
      <div class="wrap"><input id="cp-new" name="np" type="password" autocomplete="new-password" required></div>
    </div>
    <div class="lgf ${bad === 'np' ? 'bad' : ''}">
      <label for="cp-new2">再输一次<b>*</b></label>
      <div class="wrap"><input id="cp-new2" name="np2" type="password" autocomplete="new-password" required></div>
    </div>

    ${errLine()}

    <div class="lg-row">
      <span class="lg-help">忘记密码请联系项目组管理员重置</span>
      <button type="button" class="lg-link" data-lg="to-login">返回登录</button>
    </div>
    <button type="submit" class="lg-sub">保存新密码</button>
  </form>`;
}

/* 改密成功后回登录页的那一句。⚠ 一次性：渲染过就由切表单 / 再次提交时清掉 */
const okLine = (): string =>
  state.lg.ok ? `<p class="lg-ok">${IC.check}<span>${esc(state.lg.ok)}</span></p>` : '';

const errLine = (): string =>
  state.lg.err ? `<p class="lg-err">${IC.warn}<span>${esc(state.lg.err)}</span></p>` : '';

export const loginPage = definePage<{ pwd: boolean }>({
  skeleton: 'form',

  async load() {
    // 登录页自己不取数：meta 在 boot 时已经探过 /auth/me
    return { pwd: state.lg.mode === 'pwd' };
  },

  html(d) {
    return `<div class="login">${stageTop}<section class="lg-form">${d.pwd ? changePwdForm() : loginForm()}</section>${stageBottom}</div>`;
  },

  mounted(d) {
    // 账号已回填就直接聚焦密码框——重试登录时少按一次 Tab（一期的小体贴，保留）
    const sel = d.pwd
      ? (state.lg.acct ? '#cp-old' : '#cp-acct')
      : state.lg.acct ? '#lg-pwd' : '#lg-acct';
    document.querySelector<HTMLInputElement>(sel)?.focus();
    // 每次重画都是新画布，接回动效引擎（粒子状态在引擎里，不会重飞一遍）
    mountLoginFx(document.querySelector<HTMLElement>('#login-root .login'));
  },

  onEvent(e) {
    const t = e.target;
    if (!(t instanceof Element)) return;

    if (e.type === 'click' && t.closest('[data-lg="eye"]')) {
      state.lg.pwd = !state.lg.pwd;
      rerender();
      return;
    }

    /* 两张表单之间切换：清掉上一张留下的报错与成功提示，账号回填保留 */
    const to = e.type === 'click' && t.closest<HTMLElement>('[data-lg^="to-"]')?.dataset.lg;
    if (to) {
      state.lg = { ...state.lg, mode: to === 'to-pwd' ? 'pwd' : 'login', err: '', field: '', ok: '', pwd: false };
      rerender();
      return;
    }

    if (e.type !== 'submit') return;
    const form = t.closest('form');
    if (form?.hasAttribute('data-lg-form')) {
      e.preventDefault();
      void doLogin(form as HTMLFormElement);
    } else if (form?.hasAttribute('data-lg-pwd')) {
      e.preventDefault();
      void doChangePwd(form as HTMLFormElement);
    }
  },
});

/* 登录页是唯一会自己重画的页面（切明文、报错文案），走这条小路而不是整条路由 */
function rerender(): void {
  retryCurrent();
}

async function doLogin(form: HTMLFormElement): Promise<void> {
  const acct = (form.elements.namedItem('acct') as HTMLInputElement).value.trim();
  const pwd = (form.elements.namedItem('pwd') as HTMLInputElement).value;
  state.lg.err = '';
  state.lg.field = '';
  state.lg.ok = '';

  if (!acct) {
    state.lg.err = '请输入账号';
    state.lg.field = 'acct';
    return rerender();
  }
  if (!pwd) {
    state.lg.err = '请输入密码';
    state.lg.field = 'pwd';
    return rerender();
  }

  /* ⚠⚠ 认证与"进系统"必须【分开 catch】。合在一个 try 里的话，
     `loadRefs()` 失败会被当成登录失败，在密码框下面显示一句业务错误
     （W2-1 联调时的真实表现：改密其实成功了，界面却说"接口不存在"）——
     用户会以为密码错了，反复重输一个正确的密码。 */
  let u;
  try {
    u = await auth.login(acct, pwd);
  } catch (err) {
    // 这一支才是真的"账号或密码不对"，错误落在密码框上
    state.lg.err = errText(err);
    state.lg.field = 'pwd';
    state.lg.acct = acct;
    return rerender();
  }

  // ⚠ 密码到此为止，不写 state、不写 storage、不进日志
  state.lg = { err: '', field: '', pwd: false, acct, ok: '', mode: 'login' };
  meta.setMe(u);
  await enterApp();
}

/**
 * 认证通过之后的「进系统」这一步：拉齐守卫要用的元数据再放行。
 *
 * ⚠ 这里失败【不是账号密码的问题】——会话已经建好、Cookie 已经下发，人其实已经登进来了，
 *   失败的是"拉系统数据"。所以文案必须说清是哪一步出的问题；
 *   刷新页面会走 main.ts 的 boot，同样失败时那边有专门的兜底屏（指向 /api/health）。
 */
async function enterApp(): Promise<void> {
  try {
    await meta.loadRefs();
  } catch (err) {
    state.lg.err = `登录成功，但加载系统数据失败：${errText(err)}。请刷新页面重试，或联系项目组管理员。`;
    state.lg.field = '';
    return rerender();
  }
  go('#/');
}

/**
 * 登录页的自助改密：**未登录状态**下把密码换掉。
 *
 * ⚠⚠ 三步各有各的失败含义，**不能合成一个 try**（与 doLogin 同一个教训）：
 *   ① login 失败 = 账号或当前密码不对 → 错误落在「当前密码」上；
 *   ② PUT 失败 = 新密码不合规（强度规则**由服务端判**，前端不写第二套）→ 落在「新密码」上，
 *      ⚠ 此时会话已经建好了，必须补一次 logout，否则页面停在登录页、后台却挂着一条有效会话；
 *   ③ logout 失败无所谓 —— 密码已经改了，服务端 rotateSessions 也已把旧会话全踢掉。
 */
async function doChangePwd(form: HTMLFormElement): Promise<void> {
  const el = (n: string): string => (form.elements.namedItem(n) as HTMLInputElement).value;
  const acct = el('acct').trim();
  const op = el('op'), np = el('np'), np2 = el('np2');

  const fail = (field: string, msg: string): void => {
    state.lg.field = field;
    state.lg.err = msg;
    rerender();
  };
  state.lg.err = '';
  state.lg.field = '';
  state.lg.ok = '';

  if (!acct) return fail('acct', '请输入账号');
  if (!op) return fail('old', '请输入当前密码');
  if (!np) return fail('np', '请输入新密码');
  if (np !== np2) return fail('np', '两次输入的新密码不一致');
  if (np === op) return fail('np', '新密码不能与当前密码相同');

  try {
    await auth.login(acct, op);
  } catch (err) {
    state.lg.acct = acct;
    return fail('old', errText(err));
  }

  try {
    await auth.changePassword(op, np);
  } catch (err) {
    await auth.logout().catch(() => undefined); // ⚠ 见上：别把会话挂在那儿
    state.lg.acct = acct;
    return fail('np', errText(err));
  }

  // ⚠ 改完不进系统：本入口就是给"只想改个密码"的人用的
  await auth.logout().catch(() => undefined);
  meta.setMe(null);
  state.lg = { err: '', field: '', pwd: false, acct, ok: '密码已修改，请用新密码登录', mode: 'login' };
  rerender();
}
